export * from './amazonConnect';

export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  DASHBOARD: '/dashboard',
  CONTACTCENTER: '/contactcenter',
  TEAM: '/team',
  MANAGER: {
    ROOT: '/manager',
    TEAM: 'team',
    PERFORMANCE: 'performance',
    ALERTS: 'alerts',
    QUALITY_METRICS: 'quality-metrics',
    CALL_HISTORY: 'call-history',
  },
  PROFILE: '/my-profile',
} as const;

export const API_ENDPOINTS = {
  USERS: {
    BASE_URL: '/users',
  },
  AUTH: {
    ME: '/auth/me',
    MANAGER: '/auth/manager',
    DIRECT_REPORTS: '/auth/direct-reports',
  },
  AGENT_EVENTS: {
    /**
     * Base URLs for agent events routes
     * Manager: /agent-events/manager/* (with ManagerGuard)
     * Agent: /agent-events/agent/* (agents see only their own data)
     */
    BASE_URL_MANAGER: '/agent-events/manager',
    BASE_URL_AGENT: '/agent-events/agent',

    /**
     * Metric endpoints (same for both manager and agent routes)
     * Use with BASE_URL_MANAGER or BASE_URL_AGENT to construct full path
     * 13 manager endpoints, 12 agent endpoints (agent routes exclude LIVE_CALLS)
     */
    OCCUPANCY: '/occupancy',
    ADHERENCE: '/adherence',
    CONTACTS_HANDLED: '/contacts-handled',
    ABANDONMENT_RATE: '/abandonment-rate',
    AVERAGE_HANDLE_TIME: '/average-handle-time',
    AVERAGE_WRAP_TIME: '/average-wrap-time',
    AVERAGE_HOLD_TIME: '/average-hold-time',
    AVERAGE_QUEUE_ANSWER_TIME: '/average-queue-answer-time',
    SERVICE_LEVEL: '/service-level',
    DEFLECTION_RATE: '/deflection-rate',
    FIRST_CONTACT_RESOLUTION: '/first-contact-resolution',
    QUEUE_STATUS: '/queue-status',
    DASHBOARD: '/dashboard', // Agent performance dashboard - aggregated metrics
  },
  AGENTS: {
    BASE_URL: '/agents',
    CCP_STATUS_FOR_USERS: '/agents/ccp-status/for-users',
    TRANSFER_TARGETS: '/agents/transfer-targets',
    TRANSFERS_STREAM: '/agents/transfers/stream',
    TRANSFERS_REQUEST: '/agents/transfers/request',
    TRANSFERS_RESPOND: '/agents/transfers/respond',
    TRANSFERS_WARM_START: '/agents/transfers/warm/start',
    TRANSFERS_WARM_CANCEL: '/agents/transfers/warm/cancel',
    TRANSFERS_WARM_COMPLETE: '/agents/transfers/warm/complete',
    TRANSFERS_WARM_CONSULT_CONNECTED:
      '/agents/transfers/warm/consult-connected',
    TRANSFERS_ACTIVE_CONTEXT: '/agents/transfers/active-context',
  },
  ROUTING_PROFILES: {
    BASE_URL: '/routing-profiles',
    FOR_USERS: '/routing-profiles/for-users',
    MY_TEAM: '/routing-profiles/my-team',
    MY_QUEUES: '/routing-profiles/my-queues',
    DISABLED_QUEUE_QUICK_CONNECTS:
      '/routing-profiles/disabled-queue-quick-connects',
  },
  CUSTOMER: {
    BASE_URL: '/external/customer',
    INFORMATION: '/information',
    CASES: '/cases',
    SEARCH: '/search',
  },
  CALL: {
    STREAM: '/call/stream',
    MANAGER_STREAM: '/call/manager/stream',
    MONITOR: '/call/manager/monitor',
    METADATA: '/call/metadata', // /call/metadata/:contactId
    CHAT_INIT: '/chat/init',
    /** POST body: { contactId, messageId, note } — 204 on success */
    CHAT_MESSAGE_NOTES: '/chat/messages/notes',
    TRANSCRIPTS: '/call/transcripts',
    TRANSCRIPT_NOTES: '/call/transcripts/notes',
    BY_AGENT: '/call/by-agent',
    BY_AGENT_MANAGER: '/call/manager/by-agents',
    BY_WORKSPACE: '/call/by-workspace',
    MISSED_BY_USER: '/missed-call/by-user',
    BY_CONTACT: '/call', // /call/:contactId
    PAUSE: '/call', // /call/:contactId/pause
    RESUME: '/call', // /call/:contactId/resume
    RECORDING: '/call', // /call/:contactId/recording
    /** PUT body: { workspaceId } */
    LINK_CUSTOMER: '/call', // /call/:contactId/link-customer
  },
  CASE: {
    BASE_URL: '/case',
    FOR_CALL: '/for-call',
    CASE_STATUS: '/case-status',
    CASE_CATEGORIES: '/case-category',
    CASE_TYPES: '/case-type',
    CASE_TYPES_BY_CATEGORY: '/case-type', // /case/case-type/:categoryCode
    CASE_PRIORITIES: '/priority',
    CASE_SUB_CASE_TYPE_TREE: '/sub-case-type-tree',
    CREATE_CASE: '/',
    CASE_RESOLUTION: '/case-resolution',
    CASE_ORIGINATIONS: '/case-origination',
    CASE_EMPLOYEES: '/employees', // /case/employees?workspaceId=X
    CASE_USERS: '/users', // /case/users
    CASE_CONTACTS: '/contacts', // /case/contacts?workspaceId=X
    CASE_RESOLUTION_TYPES: '/case-resolution-type',
    RELATED_CASE: '/related-case', // /case/:caseId/related-case
    TIMERS: '/timers', // /case/:caseId/timers[/:timerId]
    TIME_TRACKING_CATEGORIES: '/time-tracking-categories',
  },
  ALERTS: {
    REAL_TIME: '/alerts/real-time', // SSE endpoint for live alerts
    /** POST — manager resolves alert; backend broadcasts `alert_resolved` on SSE */
    MANAGER_RESOLVE: '/alerts/manager/resolve',
  },
  QUALITY_METRICS: {
    CALL: '/quality-metrics/call',
    AGENT: '/quality-metrics/agent',
    CONTACTS: '/quality-metrics/contacts',
  },
  QUICK_SCRIPTS: {
    LIST: '/quick-scripts',
  },
  DYNAMIC_SUGGESTION: {
    BASE: '/dynamic-suggestion',
    FOR_CALL: '/dynamic-suggestion/for-call',
    TAGS: '/dynamic-suggestion/tags',
  },
} as const;

export const STORAGE_KEYS = {
  TOKEN: 'token',
  GRAPH_TOKEN: 'graphToken',
  LANGUAGE: 'i18nextLng',
} as const;
